import React, { useCallback } from 'react';
import { useKeysDown } from 'shared/hooks/use-keys-down';

enum Direction {
  Next = 'next',
  Previous = 'previous',
}

export type UsePopupMenuArrowNavigationProps = {
  containerRef: React.RefObject<HTMLElement | null>;
  // Omitted by menus with no search field: the cycle then wraps instead of parking on an input.
  inputRef?: React.RefObject<HTMLElement | null>;
  // A subtree that owns its own arrow keys: never a row, entered instead of wrapping.
  gridSelector?: string;
};

export const usePopupMenuArrowNavigation = ({
  containerRef,
  inputRef,
  gridSelector,
}: UsePopupMenuArrowNavigationProps): void => {
  const getTarget = useCallback(
    (direction: Direction): HTMLElement | null => {
      if (!containerRef.current) {
        return null;
      }

      const grid = gridSelector
        ? containerRef.current.querySelector(gridSelector)
        : null;

      const rows = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled])',
        ),
      ).filter((row) => !grid?.contains(row));

      if (!rows.length) {
        return null;
      }

      const activeElement = document.activeElement as HTMLElement;

      if (inputRef && activeElement === inputRef.current) {
        return rows[direction === Direction.Next ? 0 : rows.length - 1] ?? null;
      }

      const currentIndex = rows.indexOf(activeElement);

      if (currentIndex === -1) {
        // With a search field, focus outside the rows belongs to whoever owns it (a calendar
        // day cell). Without one, there is nothing else to defer to, so enter the list.
        return inputRef
          ? null
          : rows[direction === Direction.Next ? 0 : rows.length - 1] ?? null;
      }

      if (direction === Direction.Previous) {
        return currentIndex === 0
          ? inputRef?.current ?? rows[rows.length - 1]
          : rows[currentIndex - 1];
      }

      if (currentIndex !== rows.length - 1) {
        return rows[currentIndex + 1];
      }

      return grid?.querySelector<HTMLElement>('[tabindex="0"]') ?? rows[0];
    },
    [containerRef, inputRef, gridSelector],
  );

  const move = useCallback(
    (e: KeyboardEvent, direction: Direction) => {
      const target = getTarget(direction);

      if (!target) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();
      target.focus();
    },
    [getTarget],
  );

  const onArrowDown = useCallback(
    (e: KeyboardEvent) => move(e, Direction.Next),
    [move],
  );

  const onArrowUp = useCallback(
    (e: KeyboardEvent) => move(e, Direction.Previous),
    [move],
  );

  useKeysDown(['ArrowDown'], onArrowDown, { capture: true });
  useKeysDown(['ArrowUp'], onArrowUp, { capture: true });
};
